/**
 * profiles-api — mock/live dispatcher for BusinessProfileRegistry.
 *
 * `mock` keeps the Lovable admin shell usable without a backend. `live`
 * calls the new `/api/biz/admin/profiles/*` surface shipped by Track B
 * Sütun 2. Function signatures stay stable for the page layer.
 */
import { APP_ENV } from '@/shared/config/env';
import * as httpApi from './profiles-api-http';
import * as mockApi from './profiles-api-mock';
import type {
  BusinessProfileRecord,
  DeployFormValues,
  DeploymentRecord,
} from './types';

const useLive = APP_ENV.bizApiMode === 'live';

export async function listProfiles(): Promise<BusinessProfileRecord[]> {
  return useLive ? httpApi.listProfiles() : mockApi.listProfiles();
}

export async function getProfile(
  subVerticalKey: string,
): Promise<BusinessProfileRecord | null> {
  return useLive ? httpApi.getProfile(subVerticalKey) : mockApi.getProfile(subVerticalKey);
}

export async function listDeployments(
  subVerticalKey: string,
): Promise<DeploymentRecord[]> {
  return useLive
    ? httpApi.listDeployments(subVerticalKey)
    : mockApi.listDeployments(subVerticalKey);
}

export async function updateProfile(
  subVerticalKey: string,
  patch: Partial<BusinessProfileRecord['profile']>,
): Promise<BusinessProfileRecord> {
  return mockApi.updateProfile(subVerticalKey, patch);
}

export async function deployProfile(
  subVerticalKey: string,
  values: DeployFormValues,
  actor: { id: string; name: string },
): Promise<DeploymentRecord> {
  return useLive
    ? httpApi.deployProfile(subVerticalKey, values, actor)
    : mockApi.deployProfile(subVerticalKey, values, actor);
}

export const businessProfilesKeys = {
  all: ['admin-business-profiles'] as const,
  list: () => [...businessProfilesKeys.all, 'list'] as const,
  detail: (svk: string) =>
    [...businessProfilesKeys.all, 'detail', svk] as const,
  deployments: (svk: string) =>
    [...businessProfilesKeys.all, 'deployments', svk] as const,
};

export function getBusinessProfilesApiMode(): 'mock' | 'live' {
  return useLive ? 'live' : 'mock';
}
